import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

interface TimesheetEditData {
  timesheetHeaderId: number;
  startDate: string;
  shift: boolean;
  timesheetStatus: number;
  userId: string;
  submittedCount?: number;
  timesheetLines: any[];
  // API may return as 'TimesheetLines' (PascalCase)
  TimesheetLines?: any[];
  // User info for contractor detection (API may return as 'User' or 'user')
  User?: {
    WorkerId?: string;
    Resource?: {
      WorkerId?: string;
      WorkerType?: string;
    };
  };
  user?: {
    WorkerId?: string;
    Resource?: {
      WorkerId?: string;
      WorkerType?: string;
    };
  };
}

// Module-level state for singleton pattern (shared between all component instances)
const dialogVisible = ref<boolean>(false);
const editTimesheetId = ref<number | null>(null);
const editTimesheetData = ref<TimesheetEditData | null>(null);
const isFromNotification = ref<boolean>(false);

export default function useTimesheetEntryDialog() {
  const store = useStore();
  const router = useRouter();
  
  // Computed mode: 'create' or 'edit'
  const mode = computed(() => editTimesheetId.value ? 'edit' : 'create');

  const isOpen = () => Boolean(store.state.isOpenPopupAddTask);

  const syncFromStore = () => {
    dialogVisible.value = isOpen();
  };

  // Open for create mode
  const open = () => {
    editTimesheetId.value = null;
    editTimesheetData.value = null;
    isFromNotification.value = false;
    store.commit("SET_OPEN_POPUP_ADD_TASK", true);
    dialogVisible.value = true;
  };

  // Open for edit mode
  const openEdit = (timesheetHeader: TimesheetEditData, fromNotification: boolean = false) => {
    editTimesheetId.value = timesheetHeader.timesheetHeaderId;
    editTimesheetData.value = timesheetHeader;
    isFromNotification.value = fromNotification;
    store.commit("SET_OPEN_POPUP_ADD_TASK", true);
    dialogVisible.value = true;
  };

  const close = () => {
    store.commit("SET_OPEN_POPUP_ADD_TASK", false);
    dialogVisible.value = false;
  };

  // Close and reset edit state
  const closeEdit = () => {
    editTimesheetId.value = null;
    editTimesheetData.value = null;
    close();
  };

  const handleBeforeClose = (done?: () => void) => {
    closeEdit();
    if (done) done();
  };

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      closeEdit();
    }
  };

  onMounted(() => {
    syncFromStore();
    window.addEventListener("keydown", onKeydown);
  });

  onUnmounted(() => {
    window.removeEventListener("keydown", onKeydown);
  });

  watch(
    () => store.state.isOpenPopupAddTask,
    () => {
      syncFromStore();
    },
    { immediate: true }
  );

  router.afterEach(() => {
    if (dialogVisible.value) {
      closeEdit();
    }
  });

  return {
    dialogVisible,
    mode,
    editTimesheetId,
    editTimesheetData,
    isFromNotification,
    open,
    openEdit,
    close,
    closeEdit,
    handleBeforeClose,
  };
}
